import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { FormBuilder, FormField } from "../../../shared/components/FormBuilder";
import { Company } from "../types";

interface CompanyManagementProps {
  company: Company | undefined;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | undefined;
  onUpdate: (data: Partial<Company>) => Promise<void>;
  onRefresh: () => void;
}

export const CompanyManagement: React.FC<CompanyManagementProps> = ({
  company,
  isLoading,
  isUpdating,
  error,
  onUpdate,
  onRefresh,
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);

  // Şirket form alanları
  const companyFields: FormField[] = [
    {
      name: "name",
      label: "Şirket Adı",
      type: "text",
      required: true,
      validation: { min: 2, max: 100 },
    },
    {
      name: "legal_name",
      label: "Yasal Şirket Adı",
      type: "text",
      required: true,
      validation: { min: 2, max: 200 },
    },
    {
      name: "tax_number",
      label: "Vergi Numarası",
      type: "text",
      required: true,
      validation: { min: 10, max: 11 },
    },
    {
      name: "tax_office",
      label: "Vergi Dairesi",
      type: "text",
      required: true,
    },
    {
      name: "address",
      label: "Adres",
      type: "textarea",
      required: true,
      rows: 3,
    },
    {
      name: "phone",
      label: "Telefon",
      type: "text",
      required: true,
    },
    {
      name: "email",
      label: "E-posta",
      type: "email",
      required: true,
    },
    {
      name: "website",
      label: "Web Sitesi",
      type: "text",
      required: false,
    },
    {
      name: "currency",
      label: "Para Birimi",
      type: "select",
      required: true,
      options: [
        { value: "TRY", label: "Türk Lirası (₺)" },
        { value: "USD", label: "Amerikan Doları ($)" },
        { value: "EUR", label: "Euro (€)" },
        { value: "GBP", label: "İngiliz Sterlini (£)" },
      ],
    },
    {
      name: "timezone",
      label: "Saat Dilimi",
      type: "select",
      required: true,
      options: [
        { value: "Europe/Istanbul", label: "Türkiye (UTC+3)" },
        { value: "UTC", label: "UTC" },
        { value: "Europe/London", label: "Londra (UTC+0)" },
        { value: "America/New_York", label: "New York (UTC-5)" },
        { value: "Asia/Tokyo", label: "Tokyo (UTC+9)" },
      ],
    },
    {
      name: "language",
      label: "Dil",
      type: "select",
      required: true,
      options: [
        { value: "tr", label: "Türkçe" },
        { value: "en", label: "İngilizce" },
        { value: "de", label: "Almanca" },
        { value: "fr", label: "Fransızca" },
      ],
    },
  ];

  const handleEditSubmit = async (data: Record<string, any>) => {
    try {
      await onUpdate(data);
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Şirket güncellenirken hata:", error);
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedLogo(file);
    }
  };

  const handleLogoSubmit = async () => {
    if (selectedLogo) {
      // Logo yükleme işlemi burada yapılacak
      console.log("Logo yüklenecek:", selectedLogo);
      setLogoDialogOpen(false);
      setSelectedLogo(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography>Şirket bilgileri yükleniyor...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Ana Şirket Bilgileri */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Şirket Bilgileri"
              action={
                <Box>
                  <Tooltip title="Yenile">
                    <IconButton
                      onClick={onRefresh}
                      disabled={isLoading}
                      size="small"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setEditDialogOpen(true)}
                    disabled={isLoading || isUpdating}
                    sx={{ ml: 1 }}
                  >
                    Düzenle
                  </Button>
                </Box>
              }
            />
            <CardContent>
              {company ? (
                <Grid container spacing={3}>
                  {/* Şirket Logo ve Temel Bilgiler */}
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar
                        src={company.logo_url}
                        sx={{ width: 80, height: 80, mr: 2 }}
                      >
                        <BusinessIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" gutterBottom>
                          {company.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {company.legal_name}
                        </Typography>
                        <Chip
                          label="Aktif"
                          color="success"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Divider sx={{ width: "100%", mb: 2 }} />

                  {/* Vergi Bilgileri */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Vergi Numarası
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {company.tax_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Vergi Dairesi
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {company.tax_office}
                    </Typography>
                  </Grid>

                  {/* İletişim Bilgileri */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Adres
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {company.address}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Telefon
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {company.phone}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      E-posta
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {company.email}
                    </Typography>
                  </Grid>
                  {company.website && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Web Sitesi
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {company.website}
                      </Typography>
                    </Grid>
                  )}

                  {/* Sistem Ayarları */}
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Para Birimi
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {company.currency}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Saat Dilimi
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {company.timezone}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Dil
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {company.language === "tr" ? "Türkçe" : company.language}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography color="error">
                  Şirket bilgileri yüklenemedi
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Hızlı İşlemler */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Hızlı İşlemler" />
            <CardContent>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setLogoDialogOpen(true)}
                sx={{ mb: 2 }}
              >
                Logo Yükle
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
                sx={{ mb: 2 }}
              >
                Bilgileri Düzenle
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                disabled={isLoading}
              >
                Yenile
              </Button>
            </CardContent>
          </Card>

          {/* Şirket İstatistikleri */}
          <Card sx={{ mt: 2 }}>
            <CardHeader title="Şirket İstatistikleri" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h6" color="primary">
                    150
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Sipariş
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" color="success.main">
                    25
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktif Kullanıcı
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" color="warning.main">
                    12
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bekleyen Sipariş
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" color="info.main">
                    8
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Depo
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Şirket Düzenleme Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <BusinessIcon sx={{ mr: 1 }} />
            Şirket Bilgilerini Düzenle
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormBuilder
            fields={companyFields}
            initialData={company}
            onSubmit={handleEditSubmit}
            submitText="Güncelle"
            cancelText="İptal"
            loading={isUpdating}
            onCancel={() => setEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Logo Yükleme Dialog */}
      <Dialog
        open={logoDialogOpen}
        onClose={() => setLogoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Logo Yükle</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="logo-upload"
              type="file"
              onChange={handleLogoUpload}
            />
            <label htmlFor="logo-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Logo Seç
              </Button>
            </label>
            {selectedLogo && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Seçilen Dosya:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedLogo.name} ({(selectedLogo.size / 1024).toFixed(1)}{" "}
                  KB)
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoDialogOpen(false)}>İptal</Button>
          <Button
            onClick={handleLogoSubmit}
            variant="contained"
            disabled={!selectedLogo}
          >
            Yükle
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
